import { AlertTriangle, AlertCircle, Clock, FileText, User, MapPin } from 'lucide-react';
import type { LogbookEntry, LogbookPriority, LogbookEntryType } from '@yawi/types';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';

interface LogbookEntryCardProps {
  entry: LogbookEntry;
  onClick?: () => void;
}

const priorityConfig: Record<LogbookPriority, { color: string; bgColor: string; icon: typeof AlertTriangle }> = {
  LOW: { color: 'text-slate-600', bgColor: 'bg-slate-100', icon: FileText },
  MEDIUM: { color: 'text-blue-600', bgColor: 'bg-blue-100', icon: FileText },
  HIGH: { color: 'text-amber-600', bgColor: 'bg-amber-100', icon: AlertCircle },
  CRITICAL: { color: 'text-red-600', bgColor: 'bg-red-100', icon: AlertTriangle },
};

const typeConfig: Record<LogbookEntryType, { color: string; bgColor: string }> = {
  NOVEDAD: { color: 'text-blue-700', bgColor: 'bg-blue-50' },
  INCIDENTE: { color: 'text-red-700', bgColor: 'bg-red-50' },
  ENTREGA_TURNO: { color: 'text-purple-700', bgColor: 'bg-purple-50' },
  OBSERVACION: { color: 'text-slate-700', bgColor: 'bg-slate-50' },
};

export const LogbookEntryCard = ({ entry, onClick }: LogbookEntryCardProps) => {
  const { t } = useI18n();
  const { formatDateTime } = useDateFormatter();
  const priorityStyle = priorityConfig[entry.priority];
  const typeStyle = typeConfig[entry.type];
  const PriorityIcon = priorityStyle.icon;

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${priorityStyle.bgColor}`}>
            <PriorityIcon className={`w-4 h-4 ${priorityStyle.color}`} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 line-clamp-1">{entry.title}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeStyle.bgColor} ${typeStyle.color}`}>
                {t(`logbook.type.${entry.type.toLowerCase()}`)}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityStyle.bgColor} ${priorityStyle.color}`}>
                {t(`logbook.priority.${entry.priority.toLowerCase()}`)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{entry.description}</p>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span>{entry.guardName}</span>
          </div>
          {entry.location && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span>{entry.location}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>{formatDateTime(entry.createdAt)}</span>
        </div>
      </div>

      {/* Photos indicator */}
      {entry.photoFileIds && entry.photoFileIds.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <span className="text-xs text-slate-500">
            {entry.photoFileIds.length} foto{entry.photoFileIds.length > 1 ? 's' : ''} adjunta{entry.photoFileIds.length > 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
};
